/**
 * AnalyzersPage — the CROSS-LAB analyzer-FLEET monitoring console (MANAGEMENT 15). Every analyzer across every
 * lab, server-paginated + filtered, surfaced with its lab identity, model, discipline, protocol/transport
 * pinning, lifecycle statut, last-seen heartbeat + online flag — and a per-analyzer HEALTH/USAGE drill-down
 * (result/error volume · TAT · uptime charts) in a side drawer. MONITORING-ONLY: this plane surfaces + watches
 * the fleet; it NEVER mutates a lab's LIS config (the per-lab `lis` module owns every instrument write).
 *
 * AUTHORITY — the super-admin plane (`est_admin_plateforme`, re-checked LIVE server-side by
 * authenticatePlatform → authorizePlatform on `management.analyzer.manage`). The route already rides
 * <PlatformAdminGuard> (index.tsx); this screen ALSO renders its own <ForbiddenState> defensively and
 * short-circuits offline + the cloud-only "module not mounted on this install" (404/501) state. Full 5-state
 * DoD: forbidden / offline / cloud-absent / loading / error / empty + the live table. No PHI on this seam
 * (L-4 — the fleet reads are pure operational telemetry). Token-driven, AA, FR i18n.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Card,
  Button,
  ErrorState,
  ForbiddenState,
  Input,
  LoadingState,
  OfflineState,
  Pagination,
  Select,
  Table,
  type Column,
} from "@/ui";
import { useT, useUiStore } from "@/app";
import { ALL_MANAGEMENT_CAPS } from "../caps";
import { isManagementAbsent } from "../api";
import { useIsPlatformAdmin } from "../session";
import { PageHeader } from "../components/PageHeader";
import { Screen } from "../components/Screen";
import { StatChips } from "../components/Distribution";
import { AnalyzerHealthDrawer } from "../components/AnalyzerHealthDrawer";
import { useAnalyzerFleet } from "./analyzers.api";
import {
  ANALYZER_STATUTS,
  type AnalyzerFleetFilters,
  type AnalyzerFleetRow,
  type AnalyzerStatut,
} from "./analyzers.types";
import {
  FLEET_CONFIG,
  type HealthWindow,
  analyzerStatutTone,
  heartbeatGlyph,
  heartbeatTone,
  relativeSince,
} from "./analyzers.lib";
import { registerAnalyzersMessages } from "./analyzersMessages";

// Register the screen's i18n additively at module-eval (idempotent latch). The shell's onRegister() owns the
// nav label + the `mgmt.unavailable.*` notice; this adds the fleet table + drawer + chart keys.
registerAnalyzersMessages();

/** The online/offline heartbeat filter tri-state (the Select value; "" = both). */
type OnlineFilter = "" | "online" | "offline";

/** A short, stable lab display label (commercial name → raison sociale → reference → id). */
function labLabel(lab: AnalyzerFleetRow["laboratoire"]): string {
  if (!lab) return "—";
  return lab.nomCommercial || lab.raisonSociale || lab.reference || `#${lab.id}`;
}

export function AnalyzersPage() {
  const { t } = useT();
  const online = useUiStore((s) => s.online);
  const isAdmin = useIsPlatformAdmin();

  const [search, setSearch] = useState("");
  const [statut, setStatut] = useState<AnalyzerStatut | "">("");
  const [discipline, setDiscipline] = useState("");
  const [onlineFilter, setOnlineFilter] = useState<OnlineFilter>("");
  // LAB-07 — real SERVER pagination: the current 1-based page. A prev/next drives it off the server's
  // total/totalPages (never a client-side slice of a truncated 100-row window).
  const [page, setPage] = useState(1);
  // The health drawer target + its window. A row = open; null = closed.
  const [detail, setDetail] = useState<AnalyzerFleetRow | null>(null);
  const [window, setWindow] = useState<HealthWindow>(FLEET_CONFIG.DEFAULT_WINDOW_DAYS);

  // Any filter change resets to page 1 — otherwise a narrowing filter could strand the operator on an
  // out-of-range page (e.g. page 5 of a result set that now has 2 pages).
  useEffect(() => {
    setPage(1);
  }, [search, statut, discipline, onlineFilter]);

  const filters: AnalyzerFleetFilters = useMemo(
    () => ({
      ...(search.trim() ? { q: search.trim() } : {}),
      ...(statut ? { statut } : {}),
      ...(discipline.trim() ? { disciplineCode: discipline.trim() } : {}),
      ...(onlineFilter ? { online: onlineFilter === "online" } : {}),
      page,
      pageSize: FLEET_CONFIG.PAGE_SIZE,
    }),
    [search, statut, discipline, onlineFilter, page],
  );

  const enabled = isAdmin && online;
  const query = useAnalyzerFleet(filters, enabled);
  const rows = useMemo(() => query.data?.items ?? [], [query.data]);
  // Server-authoritative counts — the WHOLE filtered fleet, not just the loaded page.
  const total = query.data?.total ?? rows.length;
  const totalPages = query.data?.totalPages ?? 1;

  // Per-page fleet synthesis (DERIVED from the loaded page — no extra read): the parc headline counts. Refreshes
  // in lock-step with the table. Aggregate counts only (never echoes a lab identity beyond the row already shown).
  const onlineCount = useMemo(() => rows.filter((r) => r.heartbeat.online).length, [rows]);
  const offlineCount = useMemo(
    () => rows.filter((r) => !r.heartbeat.online && r.heartbeat.hasAgent).length,
    [rows],
  );
  const downCount = useMemo(() => rows.filter((r) => r.statut === "hors_service").length, [rows]);

  const header = (
    <PageHeader title={t("mgmt.analyzers.title")} subtitle={t("mgmt.analyzers.subtitle")} />
  );

  if (!isAdmin) {
    return (
      <ForbiddenState
        requiredCapabilities={ALL_MANAGEMENT_CAPS}
        message="Cette console est réservée aux administrateurs de la plateforme Pharmakon."
      />
    );
  }

  if (!online) {
    return (
      <Screen>
        {header}
        <OfflineState onRetry={() => void query.refetch()} cached={rows.length > 0} />
      </Screen>
    );
  }

  // Cloud-only module on a local box → its routes 404/501. Surface the clean "not available here" notice.
  if (isManagementAbsent(query.error)) {
    return (
      <Screen>
        {header}
        <Card title={t("mgmt.unavailable.title")}>
          <p style={{ margin: 0, color: "var(--nv-text-2, var(--nv-slate))" }}>
            {t("mgmt.unavailable.desc")}
          </p>
        </Card>
      </Screen>
    );
  }

  if (query.isLoading) {
    return (
      <Screen>
        {header}
        <LoadingState variant="table" rows={6} />
      </Screen>
    );
  }
  if (query.isError) {
    return (
      <Screen>
        {header}
        <ErrorState
          message={query.error.message}
          code={query.error.code}
          onRetry={() => void query.refetch()}
        />
      </Screen>
    );
  }

  const columns: ReadonlyArray<Column<AnalyzerFleetRow>> = [
    {
      key: "lab",
      header: t("mgmt.analyzers.col.lab"),
      sortable: true,
      sortAccessor: (r) => labLabel(r.laboratoire).toLowerCase(),
      cell: (r) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600 }}>{labLabel(r.laboratoire)}</span>
          {r.laboratoire?.wilaya && (
            <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
              {r.laboratoire.wilaya}
            </span>
          )}
        </div>
      ),
    },
    {
      key: "model",
      header: t("mgmt.analyzers.col.model"),
      sortable: true,
      sortAccessor: (r) => `${r.fabricant ?? ""} ${r.modele ?? ""}`.toLowerCase(),
      cell: (r) => (
        <div style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontWeight: 600 }}>{r.libelle}</span>
          <span style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
            {[r.fabricant, r.modele].filter(Boolean).join(" · ") || "—"}
          </span>
        </div>
      ),
    },
    {
      key: "discipline",
      header: t("mgmt.analyzers.col.discipline"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.disciplineCode ?? "",
      cell: (r) => <span className="nv-ltr-island">{r.disciplineCode ?? "—"}</span>,
    },
    {
      key: "protocol",
      header: t("mgmt.analyzers.col.protocol"),
      mono: true,
      sortable: true,
      sortAccessor: (r) => r.transport.protocole ?? "",
      cell: (r) =>
        r.transport.configured ? (
          <span className="nv-ltr-island">
            {r.transport.protocole ?? "—"}
            {r.transport.transport ? ` · ${r.transport.transport}` : ""}
          </span>
        ) : (
          <span style={{ color: "var(--nv-text-3, var(--nv-steel))" }}>
            {t("mgmt.analyzers.protocol.unconfigured")}
          </span>
        ),
    },
    {
      key: "statut",
      header: t("mgmt.analyzers.col.statut"),
      sortable: true,
      sortAccessor: (r) => ANALYZER_STATUTS.indexOf(r.statut),
      cell: (r) => (
        <Badge tone={analyzerStatutTone(r.statut)} glyph="●">
          {t(`mgmt.analyzers.statut.${r.statut}`)}
        </Badge>
      ),
    },
    {
      key: "lastSeen",
      header: t("mgmt.analyzers.col.lastSeen"),
      mono: true,
      sortable: true,
      // Sort by recency: a real timestamp beats "never" (0). Descending sort surfaces the freshest first.
      sortAccessor: (r) =>
        r.heartbeat.lastHeartbeatAt ? new Date(r.heartbeat.lastHeartbeatAt).getTime() : 0,
      cell: (r) => (
        <span className="nv-ltr-island">{relativeSince(r.heartbeat.lastHeartbeatAt)}</span>
      ),
    },
    {
      key: "health",
      header: t("mgmt.analyzers.col.health"),
      sortable: true,
      // online (2) > stale (1) > no-agent (0) — the health ranking.
      sortAccessor: (r) => (r.heartbeat.online ? 2 : r.heartbeat.hasAgent ? 1 : 0),
      cell: (r) => (
        <Badge
          tone={heartbeatTone(r.heartbeat.online, r.heartbeat.hasAgent)}
          glyph={heartbeatGlyph(r.heartbeat.online, r.heartbeat.hasAgent)}
        >
          {r.heartbeat.online
            ? t("mgmt.analyzers.heartbeat.online")
            : r.heartbeat.hasAgent
              ? t("mgmt.analyzers.heartbeat.stale")
              : t("mgmt.analyzers.heartbeat.noAgent")}
        </Badge>
      ),
    },
    {
      key: "actions",
      header: t("mgmt.analyzers.col.actions"),
      align: "end",
      cell: (r) => (
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            setWindow(FLEET_CONFIG.DEFAULT_WINDOW_DAYS);
            setDetail(r);
          }}
        >
          {t("mgmt.analyzers.action.health")}
        </Button>
      ),
    },
  ];

  const hasFilter = !!search.trim() || !!statut || !!discipline.trim() || !!onlineFilter;

  return (
    <Screen>
      {header}

      {rows.length > 0 && (
        <Card title={t("mgmt.analyzers.insight.title")}>
          <StatChips
            chips={[
              // Server-authoritative: the WHOLE filtered fleet, not just the loaded page (LAB-07).
              { key: "total", label: t("mgmt.analyzers.insight.total"), value: total },
              // The heartbeat/down chips stay PAGE-scoped (the server exposes no such aggregate) — the
              // label makes that explicit so the figures are never read as fleet-wide totals.
              {
                key: "online",
                label: t("mgmt.analyzers.insight.online"),
                value: onlineCount,
                tone: onlineCount > 0 ? "success" : "neutral",
              },
              {
                key: "offline",
                label: t("mgmt.analyzers.insight.offline"),
                value: offlineCount,
                tone: offlineCount > 0 ? "warn" : "neutral",
              },
              {
                key: "down",
                label: t("mgmt.analyzers.insight.down"),
                value: downCount,
                tone: downCount > 0 ? "danger" : "neutral",
              },
            ]}
          />
        </Card>
      )}

      <Card padded={false}>
        <div
          style={{
            display: "flex",
            gap: "var(--nv-s-3)",
            flexWrap: "wrap",
            padding: "var(--nv-s-3)",
            borderBottom: "1px solid var(--nv-border-subtle, var(--nv-line-faint))",
          }}
        >
          <div style={{ flex: "1 1 240px" }}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("mgmt.analyzers.search")}
              aria-label={t("mgmt.analyzers.search")}
            />
          </div>
          <Input
            value={discipline}
            onChange={(e) => setDiscipline(e.target.value)}
            placeholder={t("mgmt.analyzers.filter.discipline")}
            aria-label={t("mgmt.analyzers.filter.discipline")}
            className="nv-ltr-island"
          />
          <Select
            value={statut}
            onChange={(e) => setStatut(e.target.value as AnalyzerStatut | "")}
            aria-label={t("mgmt.analyzers.filter.statut")}
            options={[
              { value: "", label: t("mgmt.analyzers.filter.statut.all") },
              ...ANALYZER_STATUTS.map((s) => ({ value: s, label: t(`mgmt.analyzers.statut.${s}`) })),
            ]}
          />
          <Select
            value={onlineFilter}
            onChange={(e) => setOnlineFilter(e.target.value as OnlineFilter)}
            aria-label={t("mgmt.analyzers.filter.online")}
            options={[
              { value: "", label: t("mgmt.analyzers.filter.online.all") },
              { value: "online", label: t("mgmt.analyzers.filter.online.online") },
              { value: "offline", label: t("mgmt.analyzers.filter.online.offline") },
            ]}
          />
        </div>
        <div style={{ padding: "var(--nv-s-3)" }}>
          {/* SERVER-paginated (LAB-07): NO client pageSize — the table renders exactly the server page,
              and the shared <Pagination> walks the server's total/totalPages via `page`. */}
          <Table
            columns={columns}
            rows={rows}
            rowKey={(r) => r.id}
            caption={t("mgmt.analyzers.title")}
            emptyTitle={hasFilter ? t("mgmt.analyzers.empty.search") : t("mgmt.analyzers.empty.title")}
            {...(hasFilter ? {} : { emptyDescription: t("mgmt.analyzers.empty.desc") })}
          />
          {total > 0 && totalPages > 1 && (
            <Pagination page={page} pageCount={totalPages} total={total} onPageChange={setPage} />
          )}
        </div>
      </Card>

      <AnalyzerHealthDrawer
        row={detail}
        window={window}
        onWindowChange={setWindow}
        onClose={() => setDetail(null)}
        enabled={enabled}
      />
    </Screen>
  );
}
